/**
 * Light / dark theme.
 *
 * Three states, matching the three the stylesheet understands:
 *
 *   'light'  explicit    -> data-theme="light"
 *   'dark'   explicit    -> data-theme="dark"
 *   null     follow OS   -> no attribute, and styles/theme.css picks dark via
 *                           its @media (prefers-color-scheme: dark) block
 *
 * An explicit choice is remembered in localStorage. Following the system is
 * the default, and it tracks the system live: someone flipping their laptop
 * to dark at sunset sees ZIvapp follow without a reload.
 */

import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'

const STORAGE_KEY = 'ziv.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

const ThemeContext = createContext(null)

function readStored () {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : null
  } catch {
    // Private browsing and blocked site data both throw here
    return null
  }
}

/**
 * The operating system's current preference, as a live value.
 *
 * useSyncExternalStore rather than an effect because the first paint has to
 * be right: a one-frame flash of the wrong theme is exactly what people
 * notice on a dark-mode app.
 */
function useSystemPrefersDark () {
  return useSyncExternalStore(
    callback => {
      const query = window.matchMedia(DARK_QUERY)
      query.addEventListener('change', callback)
      return () => query.removeEventListener('change', callback)
    },
    () => window.matchMedia(DARK_QUERY).matches,
    () => false // server render: assume light, the client corrects immediately
  )
}

export function ThemeProvider ({ children }) {
  const [preference, setPreference] = useState(readStored) // 'light' | 'dark' | null
  const systemPrefersDark = useSystemPrefersDark()

  useEffect(() => {
    const root = document.documentElement
    if (preference) root.setAttribute('data-theme', preference)
    else root.removeAttribute('data-theme')

    try {
      if (preference) localStorage.setItem(STORAGE_KEY, preference)
      else localStorage.removeItem(STORAGE_KEY)
    } catch { /* storage unavailable; the attribute above still applies */ }
  }, [preference])

  const value = useMemo(() => {
    const isDark = preference ? preference === 'dark' : systemPrefersDark
    return {
      preference,                       // null when following the system
      isDark,
      isFollowingSystem: preference === null,
      setTheme: setPreference,
      useSystemTheme: () => setPreference(null),
      // Toggling from "follow system" picks the opposite of what is on screen,
      // so the first click always visibly changes something.
      toggle: () => setPreference(isDark ? 'light' : 'dark')
    }
  }, [preference, systemPrefersDark])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme () {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside ThemeProvider')
  return context
}
